import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, Wallet, Gauge, HeartHandshake, MapPin, Check } from "lucide-react";
import { useI18n } from "../i18n/I18nProvider";

const PILLAR_CONFIG = [
  { id: "easy", icon: Sparkles, color: "#7C3AED" },
  { id: "cheaper", icon: Wallet, color: "#16A34A" },
  { id: "efficient", icon: Gauge, color: "#0284C7" },
  { id: "local", icon: HeartHandshake, color: "#80CBC7" },
];

export default function WhyPage() {
  const { t } = useI18n();
  const pillars = PILLAR_CONFIG.map((p) => ({
    ...p,
    title: t(`whypage.${p.id}_title`),
    body: t(`whypage.${p.id}_body`),
    points: [
      t(`whypage.${p.id}_p1`),
      t(`whypage.${p.id}_p2`),
      t(`whypage.${p.id}_p3`),
      t(`whypage.${p.id}_p4`),
    ],
  }));

  return (
    <div data-testid="why-page">
      {/* Hero */}
      <section className="relative pt-16 pb-16 sm:pt-24 sm:pb-20 border-b border-zinc-200 overflow-hidden">
        <div className="absolute inset-0 technical-grid pointer-events-none" />
        <div className="container-oj relative">
          <div className="inline-flex items-center gap-2 rounded-full border border-oj-brand/40 bg-oj-brand/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-oj-deep" data-testid="why-live-counter">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-oj-brand opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-oj-deep" />
            </span>
            {t("whypage.counter")}
          </div>
          <div className="mt-3 text-xs font-bold uppercase tracking-[0.25em] text-zinc-500"><MapPin className="size-3.5 inline -mt-0.5 mr-1 text-oj-brand" /> {t("whypage.eyebrow")}</div>
          <h1 className="font-display mt-6 text-balance text-5xl sm:text-7xl font-black tracking-tighter leading-[0.92] text-oj-ink max-w-4xl">
            {t("whypage.h1_a")} <span className="bg-oj-brand text-oj-deep px-3 rounded-md">{t("whypage.h1_b")}</span>{t("whypage.h1_c")} <span className="bg-oj-deep text-oj-brand px-3 rounded-md">{t("whypage.h1_d")}</span>{t("whypage.h1_e")}
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-zinc-600 leading-relaxed">
            {t("whypage.intro")}
          </p>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="container-oj grid grid-cols-1 md:grid-cols-2 gap-6">
          {pillars.map((p) => (
            <article
              key={p.id}
              data-testid={`why-pillar-${p.id}`}
              className="group relative rounded-2xl border border-zinc-200 p-8 sm:p-10 bg-zinc-50/60 hover:bg-zinc-50 transition-colors overflow-hidden"
            >
              <div className="absolute top-0 left-0 right-0 h-1.5" style={{ background: p.color }} />
              <div className="size-12 rounded-md grid place-items-center text-white" style={{ background: p.color }}>
                <p.icon className="size-6" />
              </div>
              <h2 className="mt-6 font-display text-3xl sm:text-4xl font-black tracking-tighter text-oj-ink">{p.title}</h2>
              <p className="mt-3 text-zinc-700 leading-relaxed max-w-md">{p.body}</p>
              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {p.points.map((pt) => (
                  <li key={pt} className="flex items-start gap-2 text-sm text-zinc-800">
                    <Check className="size-4 mt-0.5 shrink-0" style={{ color: p.color }} /> {pt}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      {/* CTAs */}
      <section className="py-20 bg-oj-deep text-white relative overflow-hidden">
        <div className="absolute inset-0 noise opacity-30 pointer-events-none" />
        <div className="container-oj relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <h2 className="font-display text-balance text-4xl sm:text-5xl font-black tracking-tighter leading-[0.95]">
              {t("whypage.cta_h2_a")} <span className="bg-oj-brand text-oj-deep px-3 rounded-md">{t("whypage.cta_h2_b")}</span> {t("whypage.cta_h2_c")}
            </h2>
            <p className="mt-4 text-zinc-200 max-w-2xl">
              {t("whypage.cta_body")}
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-3">
            <Link
              to="/contact"
              data-testid="why-bottom-demo"
              className="inline-flex items-center justify-center gap-1.5 bg-oj-brand text-oj-deep font-semibold px-6 py-4 rounded-md hover:bg-[#6FBDB9]"
            >
              {t("whypage.book_visit")} <ArrowUpRight className="size-5" />
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center gap-1.5 border border-white/20 bg-white/5 text-white font-semibold px-6 py-4 rounded-md hover:bg-white/10"
            >
              {t("whypage.see_pricing")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
